import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CaqRequestService, LocalizacaoService } from '..';
import { VariacaoDimensaoOfertaDto } from '../../dtos/variacao-dimensao-oferta-dto';
import { DiferencaOrcamentoRealizadoProjetadoDto } from '../../dtos/diferenca-orcamento-realizado-projetado.dto';
import { TipoSimulacao } from '../../enums/simulation-type.enum';

interface PlanejamentoOrcamentarioResponse<T> {
    content: T;
}


@Injectable({
    providedIn: 'root',
})
export class PlanejamentoOrcamentarioService {
    private apiUrl = `${environment.apiUrl}/planejamento-orcamentario`;

    constructor(
        private caqRequestService: CaqRequestService,
        private localizacaoService: LocalizacaoService
    ) { }

    buscarVariacaoDimensaoOfertaPorLocalizacao(): Promise<VariacaoDimensaoOfertaDto> {
        const url = this.construirUrlVariacaoDimensaoOferta();
        return firstValueFrom(
            this.caqRequestService
                .get<PlanejamentoOrcamentarioResponse<VariacaoDimensaoOfertaDto>>(url)
                .pipe(map((res) => res.content))
        );
    }

    buscarDiferencaOrcamentoRealizadoProjetadoPorLocalizacao(): Promise<DiferencaOrcamentoRealizadoProjetadoDto> {
        const url = this.construirUrlDiferencaOrcamento();
        return firstValueFrom(
            this.caqRequestService
                .get<PlanejamentoOrcamentarioResponse<DiferencaOrcamentoRealizadoProjetadoDto>>(url)
                .pipe(map((res) => res.content))
        );
    }

    private construirUrlVariacaoDimensaoOferta(): string {
        const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();

        if (!localizacao) {
            return `${this.apiUrl}/variacao-dimensao-oferta`;
        }

        const tipo = localizacao.tipoSimulacaoSelecionado;

        if (tipo === TipoSimulacao.planejamentoNacional) {
            return `${this.apiUrl}/variacao-dimensao-oferta`;
        }

        if (localizacao.cidadeSelecionada) {
            const codMun = localizacao.cidadeSelecionada.value;
            if (tipo === TipoSimulacao.planejamento) {
                return `${this.apiUrl}/variacao-dimensao-oferta/plano/municipio/${codMun}`;
            } else if (tipo === TipoSimulacao.planejamentoPorEsferaAdmMunicipal) {
                return `${this.apiUrl}/variacao-dimensao-oferta/rede/municipio/${codMun}`;
            }
        }

        if (localizacao.estadoSelecionado) {
            const codUf = localizacao.estadoSelecionado.value;
            if (tipo === TipoSimulacao.planejamento) {
                return `${this.apiUrl}/variacao-dimensao-oferta/plano/estado/${codUf}`;
            } else if (tipo === TipoSimulacao.planejamentoPorEsferaAdmEstadual) {
                return `${this.apiUrl}/variacao-dimensao-oferta/rede/estado/${codUf}`;
            }
        }

        throw new Error('Tipo de simulação ou localização inválida');
    }

    private construirUrlDiferencaOrcamento(): string {
        const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();

        if (!localizacao) {
            return `${this.apiUrl}/diferenca-orcamento-realizado-projetado`;
        }

        const tipo = localizacao.tipoSimulacaoSelecionado;

        if (tipo === TipoSimulacao.planejamentoNacional) {
            return `${this.apiUrl}/diferenca-orcamento-realizado-projetado`;
        }

        if (localizacao.cidadeSelecionada) {
            const codMun = localizacao.cidadeSelecionada.value;
            return `${this.apiUrl}/diferenca-orcamento-realizado-projetado/municipio/${codMun}`;
        }

        if (localizacao.estadoSelecionado) {
            const codUf = localizacao.estadoSelecionado.value;
            return `${this.apiUrl}/diferenca-orcamento-realizado-projetado/estado/${codUf}`;
        }

        throw new Error('Tipo de simulação ou localização inválida');
    }

}
